/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function () {
  const SECTION_ID = 'neotec-nav-collection-panel';
  const instances = new WeakMap();

  function parseCollectionPath(rawUrl) {
    if (!rawUrl) return '';

    try {
      const absoluteUrl = new URL(rawUrl, window.location.origin);
      const match = absoluteUrl.pathname.match(/\/collections\/([^\/]+)/);
      if (!match) return '';

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const normalizedRoot = root.endsWith('/') ? root.slice(0, -1) : root;
      const localePrefix = normalizedRoot === '' ? '' : normalizedRoot;
      return `${localePrefix}/collections/${match[1]}`;
    } catch (error) {
      return '';
    }
  }

  /** 无系统箭头：隐藏原生条，右侧细轨 + 可拖拽滑块 */
  class NeotecProductsScrollbar {
    constructor(panel) {
      this.panel = panel;
      this.row = panel && panel.closest('[data-neotec-products-row]');
      this.rail = this.row && this.row.querySelector('[data-neotec-scrollbar]');
      this.track = this.rail && this.rail.querySelector('.neotec-mega-nav__products-scrollbar-track');
      this.thumb = this.row && this.row.querySelector('[data-neotec-scrollbar-thumb]');
      if (!this.panel || !this.rail || !this.track || !this.thumb) {
        this.panel = null;
        return;
      }

      this._onScroll = this._onScroll.bind(this);
      this._onThumbPointerDown = this._onThumbPointerDown.bind(this);
      this._onTrackPointerDown = this._onTrackPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this._drag = null;
      this._raf = 0;

      this.panel.addEventListener('scroll', this._onScroll, { passive: true });
      this.thumb.addEventListener('pointerdown', this._onThumbPointerDown);
      this.track.addEventListener('pointerdown', this._onTrackPointerDown);
      this._ro = new ResizeObserver(() => this.scheduleUpdate());
      this._ro.observe(this.panel);

      this.scheduleUpdate();
    }

    destroy() {
      if (!this.panel) return;
      this.panel.removeEventListener('scroll', this._onScroll);
      if (this.thumb) this.thumb.removeEventListener('pointerdown', this._onThumbPointerDown);
      if (this.track) this.track.removeEventListener('pointerdown', this._onTrackPointerDown);
      if (this._ro) this._ro.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      this.panel = null;
    }

    scheduleUpdate() {
      if (!this.panel) return;
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this.update();
      });
    }

    update() {
      if (!this.panel || !this.rail || !this.track || !this.thumb) return;

      const maxScroll = this.panel.scrollHeight - this.panel.clientHeight;
      if (maxScroll <= 1) {
        this.rail.setAttribute('hidden', '');
        return;
      }

      this.rail.removeAttribute('hidden');

      const trackH = this.track.clientHeight;
      if (trackH < 24) return;

      const ratio = this.panel.clientHeight / this.panel.scrollHeight;
      let thumbH = Math.max(28, Math.floor(ratio * trackH));
      thumbH = Math.min(thumbH, trackH);

      const denom = Math.max(1, maxScroll);
      const scrollRatio = this.panel.scrollTop / denom;
      const maxThumbTop = trackH - thumbH;
      const thumbTop = Math.round(scrollRatio * maxThumbTop);

      this.thumb.style.height = `${thumbH}px`;
      this.thumb.style.top = `${thumbTop}px`;
    }

    _onScroll() {
      this.scheduleUpdate();
    }

    _onThumbPointerDown(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const maxScroll = Math.max(1, this.panel.scrollHeight - this.panel.clientHeight);
      const trackH = this.track.clientHeight;
      const ratio = this.panel.clientHeight / this.panel.scrollHeight;
      const thumbH = Math.min(Math.max(28, Math.floor(ratio * trackH)), trackH);
      const thumbRect = this.thumb.getBoundingClientRect();
      this._drag = {
        pointerId: e.pointerId,
        offsetY: e.clientY - thumbRect.top,
        maxScroll,
        trackH,
        thumbH,
      };
      try {
        this.thumb.setPointerCapture(e.pointerId);
      } catch (err) {
        /* noop */
      }
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._drag || e.pointerId !== this._drag.pointerId) return;
      const trackRect = this.track.getBoundingClientRect();
      let y = e.clientY - trackRect.top - this._drag.offsetY;
      const maxThumbTop = Math.max(1, this._drag.trackH - this._drag.thumbH);
      y = Math.max(0, Math.min(maxThumbTop, y));
      this.panel.scrollTop = (y / maxThumbTop) * this._drag.maxScroll;
    }

    _onPointerUp(e) {
      if (!this._drag) return;
      if (e.pointerId !== this._drag.pointerId) return;
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      try {
        this.thumb.releasePointerCapture(this._drag.pointerId);
      } catch (err) {
        /* noop */
      }
      this._drag = null;
    }

    _onTrackPointerDown(e) {
      if (e.button !== 0 || e.target !== this.track) return;
      const maxScroll = this.panel.scrollHeight - this.panel.clientHeight;
      if (maxScroll <= 1) return;
      const trackH = this.track.clientHeight;
      const ratio = this.panel.clientHeight / this.panel.scrollHeight;
      const thumbH = Math.min(Math.max(28, Math.floor(ratio * trackH)), trackH);
      const maxThumbTop = trackH - thumbH;
      const thumbTop = (this.panel.scrollTop / maxScroll) * maxThumbTop;
      const rect = this.track.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      if (clickY >= thumbTop && clickY <= thumbTop + thumbH) return;
      const targetTop = Math.max(0, Math.min(maxThumbTop, clickY - thumbH / 2));
      this.panel.scrollTop = (targetTop / maxThumbTop) * maxScroll;
    }
  }

  class NeotecAsyncMegaMenu {
    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('[data-collection-panel]');
      this.secondaryButtons = Array.from(element.querySelectorAll('[data-action="activate-secondary"]'));
      this.seriesPanels = Array.from(element.querySelectorAll('[data-series-panel]'));
      this.cache = new Map();
      this.abortController = null;
      this.scrollbar = null;

      if (!this.panel || this.secondaryButtons.length === 0 || this.seriesPanels.length === 0) {
        return;
      }

      this.loadingLabel = element.dataset.loadingLabel || 'Loading...';
      this.errorLabel = element.dataset.errorLabel || 'Failed to load';
      this.emptyLabel = element.dataset.emptyLabel || 'No products found';

      this._bindEvents();
      this.scrollbar = new NeotecProductsScrollbar(this.panel);
      this._activateSecondary(0);

      const defaultUrl = element.dataset.defaultCollectionUrl || '';
      if (defaultUrl) {
        this._loadCollection(defaultUrl, null);
      }
    }

    destroy() {
      if (this.scrollbar) {
        this.scrollbar.destroy();
        this.scrollbar = null;
      }

      this.secondaryButtons.forEach((button) => {
        button.removeEventListener('mouseenter', button._neotecEnterHandler);
        button.removeEventListener('focus', button._neotecFocusHandler);
      });

      this.seriesPanels.forEach((panel) => {
        panel.querySelectorAll('[data-action="load-collection"]').forEach((button) => {
          button.removeEventListener('mouseenter', button._neotecEnterHandler);
          button.removeEventListener('focus', button._neotecFocusHandler);
          button.removeEventListener('click', button._neotecClickHandler);
        });
      });

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    _syncScrollbar() {
      if (this.scrollbar) this.scrollbar.scheduleUpdate();
    }

    _bindEvents() {
      this.secondaryButtons.forEach((button, index) => {
        button._neotecEnterHandler = () => this._activateSecondary(index);
        button._neotecFocusHandler = () => this._activateSecondary(index);
        button.addEventListener('mouseenter', button._neotecEnterHandler);
        button.addEventListener('focus', button._neotecFocusHandler);
      });

      this.seriesPanels.forEach((panel) => {
        panel.querySelectorAll('[data-action="load-collection"]').forEach((button) => {
          button._neotecEnterHandler = () => this._loadCollection(button.dataset.collectionUrl, button);
          button._neotecFocusHandler = () => this._loadCollection(button.dataset.collectionUrl, button);
          button._neotecClickHandler = () => this._loadCollection(button.dataset.collectionUrl, button);

          button.addEventListener('mouseenter', button._neotecEnterHandler);
          button.addEventListener('focus', button._neotecFocusHandler);
          button.addEventListener('click', button._neotecClickHandler);
        });
      });
    }

    _activateSecondary(index) {
      this.secondaryButtons.forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', index === buttonIndex);
      });

      this.seriesPanels.forEach((panel, panelIndex) => {
        panel.classList.toggle('is-hidden', index !== panelIndex);
      });

      const activePanel = this.seriesPanels[index];
      if (!activePanel) return;

      const firstSeriesButton = activePanel.querySelector('[data-action="load-collection"]');
      if (firstSeriesButton) {
        this._loadCollection(firstSeriesButton.dataset.collectionUrl, firstSeriesButton);
      } else {
        this.panel.innerHTML = `<div class="neotec-mega-nav__placeholder">${this.emptyLabel}</div>`;
        this._syncScrollbar();
      }
    }

    _setActiveSeriesButton(activeButton) {
      this.seriesPanels.forEach((panel) => {
        panel.querySelectorAll('[data-action="load-collection"]').forEach((button) => {
          button.classList.toggle('is-active', button === activeButton);
        });
      });
    }

    _loadCollection(rawUrl, triggerButton) {
      const collectionPath = parseCollectionPath(rawUrl);
      if (!collectionPath) {
        this.panel.innerHTML = `<div class="neotec-mega-nav__placeholder">${this.emptyLabel}</div>`;
        this._syncScrollbar();
        return;
      }

      if (triggerButton) {
        this._setActiveSeriesButton(triggerButton);
      }

      if (this.cache.has(collectionPath)) {
        this.panel.innerHTML = this.cache.get(collectionPath);
        this._syncScrollbar();
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      const requestUrl = `${collectionPath}?section_id=${SECTION_ID}`;

      this.panel.innerHTML = `<div class="neotec-mega-nav__placeholder">${this.loadingLabel}</div>`;
      this._syncScrollbar();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          this.cache.set(collectionPath, html);
          this.panel.innerHTML = html;
          this._syncScrollbar();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.panel.innerHTML = `<div class="neotec-mega-nav__placeholder">${this.errorLabel}</div>`;
          this._syncScrollbar();
        });
    }
  }

  function mount(root) {
    const scope = root || document;
    scope.querySelectorAll('.mega-menu[data-neotec-async="true"]').forEach((element) => {
      if (instances.has(element)) return;
      instances.set(element, new NeotecAsyncMegaMenu(element));
    });
  }

  function unmount(root) {
    const scope = root || document;
    scope.querySelectorAll('.mega-menu[data-neotec-async="true"]').forEach((element) => {
      const instance = instances.get(element);
      if (!instance) return;
      instance.destroy();
      instances.delete(element);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    mount(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    unmount(event.target);
  });
})();
